
import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { url } from 'helpers/url_helper';

const PPTViewer = ({ preSignedUrl, courseId, userId, onSlideChange }) => {
    const containerRef = useRef(null);
    const slideContainerRef = useRef(null);
    const [numSlides, setNumSlides] = useState(null);
    const [slideNumber, setSlideNumber] = useState(1);
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [slideImage, setSlideImage] = useState(null);
    const [gifs, setGifs] = useState([]);
    const [showGifPopup, setShowGifPopup] = useState(false);
    const [currentGifIndex, setCurrentGifIndex] = useState(0);
    const [gifLoading, setGifLoading] = useState(false);
    const [loadedGifs, setLoadedGifs] = useState({});
    
    const gifElementsRef = useRef(new Map());
    const API_BASE_URL = url;

    // Fetch metadata on mount
    useEffect(() => {
        fetchMetadata();
    }, [preSignedUrl]);

    // Fetch metadata
    const fetchMetadata = async () => {
        try {
            setIsLoading(true);
            setError(null);

            const response = await axios.get(`${API_BASE_URL}ppt/metadata`, {
                params: { pptUrl: preSignedUrl }
            });

            if (response.data.success) {
                setNumSlides(response.data.slideCount);
                // Load first slide
                await loadSlide(1);
            } else {
                throw new Error(response.data.error || 'Failed to load metadata');
            }
            
        } catch (err) {
            console.error('Error fetching PPT metadata:', err);
            setError('Failed to load PPT: ' + err.message);
            setIsLoading(false);
        }
    };

    // Load slide with GIFs - wrapped in useCallback to avoid dependency issues
    const loadSlide = useCallback(async (slideNum) => {
        try {
            setIsLoading(true);
            setError(null);
            setSlideImage(null);
            setGifs([]);
            setLoadedGifs({});
            setCurrentGifIndex(0);
            
            // Clear previous GIF elements
            clearGifElements();
            
            const scale = isFullScreen ? 2 : 1.5;
            const encodedPptUrl = encodeURIComponent(preSignedUrl);
            
            // Fetch enhanced slide data
            const response = await axios.get(`${API_BASE_URL}ppt/render-enhanced`, {
                params: {
                    pptUrl: encodedPptUrl,
                    slideNumber: slideNum,
                    scale: scale
                }
            });

            if (response.data.success) {
                // Set slide image
                setSlideImage(`data:image/png;base64,${response.data.slideImage}`);
                
                // Store GIFs
                if (response.data.gifs && response.data.gifs.length > 0) {
                    const gifsWithInfo = response.data.gifs.map(gif => ({
                        ...gif,
                        loaded: false,
                        url: null,
                        error: false
                    }));
                    setGifs(gifsWithInfo);
                    console.log(`Found ${response.data.gifs.length} GIFs in slide ${slideNum}`);
                    
                    // Pre-load all GIFs
                    preloadAllGifs(gifsWithInfo);
                }
                
                // Update slide number
                setSlideNumber(slideNum);
                
                // Update progress
                updateProgress(slideNum);
                
                // Notify parent component
                if (onSlideChange) {
                    onSlideChange(slideNum);
                }
                
            } else {
                throw new Error(response.data.error || 'Failed to render slide');
            }
            
            setIsLoading(false);
            
        } catch (err) {
            console.error('Error loading slide:', err);
            setError('Failed to load slide: ' + err.message);
            setIsLoading(false);
        }
    }, [isFullScreen, preSignedUrl, onSlideChange, courseId, userId]);

    // Pre-load all GIFs
    const preloadAllGifs = useCallback(async (gifInfos) => {
        const loadingPromises = gifInfos.map(async (gif, index) => {
            try {
                // Load GIF using the simple endpoint
                const gifUrl = `${API_BASE_URL}ppt/gif/simple/${gif.id}`;
                
                // Create image element to preload
                const img = new Image();
                
                return new Promise((resolve) => {
                    img.onload = () => {
                        console.log(`Preloaded GIF: ${gif.id}`);
                        setLoadedGifs(prev => ({
                            ...prev,
                            [gif.id]: {
                                url: gifUrl,
                                loaded: true,
                                error: false
                            }
                        }));
                        resolve(true);
                    };
                    
                    img.onerror = () => {
                        console.error(`Failed to preload GIF: ${gif.id}`);
                        setLoadedGifs(prev => ({
                            ...prev,
                            [gif.id]: {
                                url: null,
                                loaded: false,
                                error: true
                            }
                        }));
                        resolve(false);
                    };
                    
                    img.src = gifUrl;
                });
            } catch (err) {
                console.error(`Error preloading GIF ${gif.id}:`, err);
                return false;
            }
        });

        // Load all GIFs in parallel
        await Promise.all(loadingPromises);
        console.log('All GIFs preloaded');
    }, [API_BASE_URL]);

    // Clear GIF elements
    const clearGifElements = useCallback(() => {
        gifElementsRef.current.forEach((gif, id) => {
            if (gif.element && gif.element.parentNode) {
                gif.element.parentNode.removeChild(gif.element);
            }
        });
        gifElementsRef.current.clear();
    }, []);

    // Update progress
    const updateProgress = useCallback(async (slideNum) => {
        try {
            // Only update if we have courseId and userId
            if (courseId && userId && courseId !== 'null' && userId !== 'null') {
                await axios.post(`${API_BASE_URL}api/updateProgress`, null, {
                    params: {
                        courseId: courseId,
                        userId: userId,
                        slideNumber: slideNum
                    }
                });
                console.log(`Progress updated: Slide ${slideNum}`);
            }
        } catch (err) {
            console.error('Error updating progress:', err);
            // Silently fail - progress update is not critical
        }
    }, [API_BASE_URL, courseId, userId]);

    // Navigation
    const goToPrevSlide = useCallback(() => {
        if (slideNumber > 1) {
            loadSlide(slideNumber - 1);
        }
    }, [slideNumber, loadSlide]);

    const goToNextSlide = useCallback(() => {
        if (slideNumber < numSlides) {
            loadSlide(slideNumber + 1);
        }
    }, [slideNumber, numSlides, loadSlide]);

    const goToSlide = useCallback((slideNum) => {
        if (slideNum >= 1 && slideNum <= numSlides) {
            loadSlide(slideNum);
        }
    }, [numSlides, loadSlide]);

    // Open GIF popup
    const openGifPopup = useCallback(() => {
        if (gifs.length > 0) {
            setShowGifPopup(true);
            setCurrentGifIndex(0);
        }
    }, [gifs.length]);

    // Close GIF popup
    const closeGifPopup = useCallback(() => {
        setShowGifPopup(false);
        setCurrentGifIndex(0);
    }, []);

    // Next GIF in popup
    const nextGif = useCallback(() => {
        if (gifs.length > 0) {
            setCurrentGifIndex((prev) => (prev + 1) % gifs.length);
        }
    }, [gifs.length]);

    // Previous GIF in popup
    const prevGif = useCallback(() => {
        if (gifs.length > 0) {
            setCurrentGifIndex((prev) => (prev - 1 + gifs.length) % gifs.length);
        }
    }, [gifs.length]);

    // Load specific GIF
    const loadSpecificGif = useCallback(async (gifId) => {
        try {
            setGifLoading(true);
            const gifUrl = `${API_BASE_URL}ppt/gif/simple/${gifId}`;
            
            setLoadedGifs(prev => ({
                ...prev,
                [gifId]: {
                    url: gifUrl,
                    loaded: true,
                    error: false
                }
            }));
            
            setGifLoading(false);
        } catch (err) {
            console.error(`Error loading GIF ${gifId}:`, err);
            setLoadedGifs(prev => ({
                ...prev,
                [gifId]: {
                    url: null,
                    loaded: false,
                    error: true
                }
            }));
            setGifLoading(false);
        }
    }, [API_BASE_URL]);

    // Toggle fullscreen
    const toggleFullScreen = useCallback(() => {
        if (!isFullScreen) {
            if (containerRef.current.requestFullscreen) {
                containerRef.current.requestFullscreen();
            }
        } else {
            if (document.exitFullscreen) {
                document.exitFullscreen();
            }
        }
    }, [isFullScreen]);

    // Handle keyboard navigation
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'ArrowLeft') {
                if (showGifPopup && gifs.length > 1) {
                    e.preventDefault();
                    prevGif();
                } else {
                    goToPrevSlide();
                }
            }
            if (e.key === 'ArrowRight') {
                if (showGifPopup && gifs.length > 1) {
                    e.preventDefault();
                    nextGif();
                } else {
                    goToNextSlide();
                }
            }
            if (e.key === ' ') e.preventDefault(); // Prevent space from scrolling
            if (e.key === 'g' || e.key === 'G') {
                e.preventDefault();
                if (gifs.length > 0) {
                    if (showGifPopup) {
                        closeGifPopup();
                    } else {
                        openGifPopup();
                    }
                }
            }
            if (e.key === 'Escape') {
                if (showGifPopup) {
                    closeGifPopup();
                }
            }
            if (e.key >= '1' && e.key <= '9') {
                const num = parseInt(e.key);
                if (num <= numSlides) {
                    goToSlide(num);
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [
        slideNumber, 
        numSlides, 
        showGifPopup, 
        gifs, 
        currentGifIndex, 
        goToPrevSlide, 
        goToNextSlide, 
        goToSlide, 
        prevGif, 
        nextGif, 
        openGifPopup, 
        closeGifPopup
    ]);

    // Handle fullscreen changes
    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullScreen(!!document.fullscreenElement);
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);
        return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
    }, []);

    // Reload slide when fullscreen changes
    useEffect(() => {
        if (slideNumber > 0) {
            loadSlide(slideNumber);
        }
    }, [isFullScreen, loadSlide, slideNumber]);

    // Render slide numbers for quick navigation
    const renderSlideNumbers = () => {
        if (!numSlides || numSlides <= 1) return null;
        
        const slides = [];
        const maxVisible = 10;
        let startSlide = Math.max(1, slideNumber - Math.floor(maxVisible / 2));
        let endSlide = Math.min(numSlides, startSlide + maxVisible - 1);
        
        if (endSlide - startSlide + 1 < maxVisible) {
            startSlide = Math.max(1, endSlide - maxVisible + 1);
        }
        
        for (let i = startSlide; i <= endSlide; i++) {
            slides.push(
                <button
                    key={i}
                    onClick={() => goToSlide(i)}
                    style={{
                        padding: '4px 8px',
                        backgroundColor: i === slideNumber ? '#3498db' : '#ecf0f1',
                        color: i === slideNumber ? 'white' : '#2c3e50',
                        border: '1px solid #bdc3c7',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: i === slideNumber ? 'bold' : 'normal',
                        minWidth: '30px'
                    }}
                >
                    {i}
                </button>
            );
        }
        
        return slides;
    };

    const currentGif = gifs[currentGifIndex] || null;
    const currentGifLoaded = currentGif ? loadedGifs[currentGif.id] : null;

    return (
        <div
            ref={containerRef}
            style={{
                position: 'relative',
                height: isFullScreen ? '100vh' : '100vh',
                overflow: 'hidden',
                userSelect: 'none',
                backgroundColor: '#f5f5f5',
                display: 'flex',
                flexDirection: 'column'
            }}
            onContextMenu={(e) => e.preventDefault()}
        >
            {/* Header */}
            <div style={headerStyle}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    <button onClick={goToPrevSlide} disabled={slideNumber <= 1} style={navButtonStyle(slideNumber <= 1)}>
                        ◀ Previous
                    </button>
                    
                    <div style={slideCounterStyle}>
                        <span>Slide</span>
                        <span style={{ fontWeight: 'bold', margin: '0 5px' }}>{slideNumber}</span>
                        <span>of</span>
                        <span style={{ fontWeight: 'bold', marginLeft: '5px' }}>{numSlides || '--'}</span>
                    </div>
                    
                    <button onClick={goToNextSlide} disabled={slideNumber >= numSlides} style={navButtonStyle(slideNumber >= numSlides)}>
                        Next ▶
                    </button>
                    
                    {gifs.length > 0 && (
                        <button onClick={openGifPopup} style={gifButtonStyle}>
                            Fullscreen GIFs ({gifs.length})
                        </button>
                    )}
                </div>
                
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <button onClick={toggleFullScreen} style={fullscreenButtonStyle}>
                        {isFullScreen ? '⛶ Exit Fullscreen' : '⛶ Fullscreen'}
                    </button>
                </div>
            </div>

            {/* Slide Numbers Navigation */}
            {numSlides > 1 && (
                <div style={{
                    backgroundColor: 'rgba(236, 240, 241, 0.9)',
                    padding: '10px',
                    display: 'flex',
                    justifyContent: 'center',
                    gap: '5px',
                    flexWrap: 'wrap',
                    borderBottom: '1px solid #bdc3c7'
                }}>
                    {renderSlideNumbers()}
                </div>
            )}

            {/* Main Slide Container */}
            <div
                ref={slideContainerRef}
                style={{
                    flex: 1,
                    position: 'relative',
                    overflow: 'auto',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: isFullScreen ? '40px' : '20px',
                    backgroundColor: '#ecf0f1'
                }}
            >
                {isLoading ? (
                    <div style={loadingStyle}>
                        <div className="loading-spinner" />
                        <p style={{ marginTop: '20px', color: '#7f8c8d' }}>Loading slide {slideNumber}...</p>
                        {gifs.length > 0 && (
                            <p style={{ marginTop: '10px', color: '#9b59b6', fontSize: '14px' }}>
                                Preloading {gifs.length} GIF{gifs.length !== 1 ? 's' : ''}...
                            </p>
                        )}
                    </div>
                ) : error ? (
                    <div style={errorStyle}>
                        <p style={{ color: '#e74c3c', marginBottom: '20px' }}>{error}</p>
                        <button onClick={() => loadSlide(1)} style={retryButtonStyle}>
                            Retry from First Slide
                        </button>
                    </div>
                ) : slideImage ? (
                    <>
                        <img
                            src={slideImage}
                            alt={`PPT Slide ${slideNumber}`}
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'contain',
                                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                                backgroundColor: 'white'
                            }}
                        />
                        
                        {/* Info overlay */}
                        {gifs.length > 0 && !showGifPopup && (
                            <div style={infoOverlayStyle}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <div style={{ 
                                        backgroundColor: '#9b59b6', 
                                        color: 'white',
                                        padding: '4px 8px',
                                        borderRadius: '4px',
                                        fontWeight: 'bold',
                                        fontSize: '14px'
                                    }}>
                                        {gifs.length} GIF{gifs.length !== 1 ? 's' : ''}
                                    </div>
                                    <div>
                                        <div style={{ fontWeight: 'bold' }}>Click "Fullscreen GIFs" or press G</div>
                                        <div style={{ fontSize: '11px', opacity: 0.9 }}>to view all GIFs in fullscreen</div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                ) : null}
            </div>

            {/* Footer */}
            <div style={footerStyle}>
                <div style={{ fontSize: '12px', color: '#7f8c8d' }}>
                    <span>Slide {slideNumber} of {numSlides || '--'}</span>
                    {gifs.length > 0 && (
                        <>
                            <span style={{ marginLeft: '20px', color: '#9b59b6' }}>
                                ● {gifs.length} GIF{gifs.length !== 1 ? 's' : ''} available
                            </span>
                            <span style={{ marginLeft: '20px' }}>Press G for fullscreen GIF viewer</span>
                        </>
                    )}
                    <span style={{ marginLeft: '20px' }}>Arrow keys to navigate</span>
                    <span style={{ marginLeft: '20px' }}>ESC to close</span>
                </div>
            </div>

            {/* Fullscreen GIF Popup */}
            {showGifPopup && gifs.length > 0 && (
                <div style={modalOverlayStyle} onClick={closeGifPopup}>
                    <div 
                        style={modalContentStyle}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Header */}
                        <div style={modalHeaderStyle}>
                            <div style={headerContentStyle}>
                                <div style={titleSectionStyle}>
                                    <h2 style={modalTitleStyle}>
                                        GIF Viewer • Slide {slideNumber}
                                    </h2>
                                    <div style={counterStyle}>
                                        <span style={currentCounterStyle}>{currentGifIndex + 1}</span>
                                        <span style={totalCounterStyle}>/{gifs.length}</span>
                                    </div>
                                </div>
                                
                                <button onClick={closeGifPopup} style={closeButtonStyle}>
                                    ✕ Close
                                </button>
                            </div>
                        </div>
                        
                        {/* Main GIF Display */}
                        <div style={gifDisplayContainer}>
                            {gifLoading ? (
                                <div style={loadingContainerStyle}>
                                    <div className="loading-spinner" style={{ width: '60px', height: '60px' }} />
                                    <p style={loadingTextStyle}>Loading GIF...</p>
                                </div>
                            ) : currentGifLoaded?.error ? (
                                <div style={errorContainerStyle}>
                                    <div style={errorIconStyle}>⚠️</div>
                                    <p style={errorTextStyle}>Failed to load this GIF</p>
                                    <button 
                                        onClick={() => loadSpecificGif(currentGif.id)}
                                        style={retryButtonStyle}
                                    >
                                        Try Again
                                    </button>
                                </div>
                            ) : currentGifLoaded?.loaded && currentGifLoaded.url ? (
                                <div style={loadedGifContainer}>
                                    <img
                                        src={currentGifLoaded.url}
                                        alt={`GIF ${currentGifIndex + 1}`}
                                        style={gifImageFullStyle}
                                        onContextMenu={(e) => e.preventDefault()}
                                        draggable="false"
                                    />
                                </div>
                            ) : (
                                <div style={loadingContainerStyle}>
                                    <div className="loading-spinner" style={{ width: '60px', height: '60px' }} />
                                    <p style={loadingTextStyle}>Preparing GIF...</p>
                                    <button 
                                        onClick={() => loadSpecificGif(currentGif.id)}
                                        style={retryButtonStyle}
                                    >
                                        Load Now
                                    </button>
                                </div>
                            )}
                            
                            {/* Navigation Arrows */}
                            {gifs.length > 1 && (
                                <>
                                    <button 
                                        onClick={prevGif}
                                        style={navArrowStyle('left')}
                                    >
                                        ◀
                                    </button>
                                    <button 
                                        onClick={nextGif}
                                        style={navArrowStyle('right')}
                                    >
                                        ▶
                                    </button>
                                </>
                            )}
                        </div>
                        
                        {/* Footer Controls */}
                        <div style={modalFooterStyle}>
                            <div style={footerControlsStyle}>
                                {gifs.length > 1 && (
                                    <div style={gifSelectorStyle}>
                                        {gifs.map((gif, index) => (
                                            <button
                                                key={gif.id}
                                                onClick={() => setCurrentGifIndex(index)}
                                                style={{
                                                    ...gifSelectorButtonStyle,
                                                    backgroundColor: index === currentGifIndex ? '#9b59b6' : '#ecf0f1',
                                                    color: index === currentGifIndex ? 'white' : '#7f8c8d',
                                                }}
                                                title={`GIF ${index + 1}`}
                                            >
                                                {index + 1}
                                                {loadedGifs[gif.id]?.loaded && !loadedGifs[gif.id]?.error && (
                                                    <span style={loadedIndicatorStyle}>●</span>
                                                )}
                                                {loadedGifs[gif.id]?.error && (
                                                    <span style={errorIndicatorStyle}>⚠</span>
                                                )}
                                            </button>
                                        ))}
                                    </div>
                                )}
                                
                                <div style={footerInfoStyle}>
                                    <p style={infoTextStyle}>
                                        Use ← → arrow keys to navigate • Press ESC to close
                                    </p>
                                    {currentGif && (
                                        <p style={gifInfoStyle}>
                                            {currentGif.frameCount ? `${currentGif.frameCount} frames` : 'Static image'} • 
                                            {currentGif.size ? ` ${Math.round(currentGif.size / 1024)} KB` : ''}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* CSS Styles */}
            <style>
                {`
                .loading-spinner {
                    border: 4px solid #f3f3f3;
                    border-top: 4px solid #3498db;
                    border-radius: 50%;
                    width: 40px;
                    height: 40px;
                    animation: spin 1s linear infinite;
                }
                
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
                
                .ppt-gif-overlay:hover {
                    cursor: help;
                }
                
                .static-gif {
                    transition: all 0.3s ease;
                }
                
                .gif-loader {
                    animation: spin 1s linear infinite;
                }
                `}
            </style>
        </div>
    );
};

// Style objects
const headerStyle = {
    backgroundColor: '#2c3e50',
    color: 'white',
    padding: '12px 20px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
    zIndex: 100
};

const navButtonStyle = (disabled) => ({
    padding: '8px 16px',
    backgroundColor: disabled ? '#95a5a6' : '#3498db',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontWeight: 'bold',
    transition: 'all 0.2s',
    '&:hover:not(:disabled)': {
        backgroundColor: '#2980b9'
    }
});

const slideCounterStyle = {
    backgroundColor: 'rgba(255,255,255,0.2)',
    padding: '8px 15px',
    borderRadius: '20px',
    fontSize: '14px',
    fontWeight: '600'
};

const gifButtonStyle = {
    padding: '8px 16px',
    backgroundColor: '#9b59b6',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontWeight: 'bold',
    transition: 'all 0.2s',
    '&:hover': {
        backgroundColor: '#8e44ad',
        transform: 'translateY(-1px)'
    }
};

const fullscreenButtonStyle = {
    padding: '8px 16px',
    backgroundColor: '#16a085',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontWeight: 'bold',
    transition: 'all 0.2s',
    '&:hover': {
        backgroundColor: '#138d75'
    }
};

const loadingStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    textAlign: 'center'
};

const errorStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    textAlign: 'center'
};

const retryButtonStyle = {
    padding: '10px 20px',
    backgroundColor: '#3498db',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontWeight: 'bold',
    '&:hover': {
        backgroundColor: '#2980b9'
    }
};

const infoOverlayStyle = {
    position: 'absolute',
    bottom: '20px',
    right: '20px',
    backgroundColor: 'rgba(0,0,0,0.85)',
    color: 'white',
    padding: '10px 15px',
    borderRadius: '8px',
    fontSize: '14px',
    zIndex: 5,
    pointerEvents: 'none',
    backdropFilter: 'blur(4px)',
    border: '1px solid rgba(155, 89, 182, 0.3)'
};

const footerStyle = {
    backgroundColor: 'rgba(0,0,0,0.9)',
    color: 'white',
    padding: '10px 20px',
    fontSize: '12px',
    textAlign: 'center',
    borderTop: '1px solid rgba(255,255,255,0.1)'
};

// Fullscreen Popup Styles
const modalOverlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.95)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 2000,
    backdropFilter: 'blur(10px)'
};

const modalContentStyle = {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    width: '95%',
    maxWidth: '1200px',
    height: '90vh',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    boxShadow: '0 30px 60px rgba(0,0,0,0.5)',
    border: '1px solid rgba(155, 89, 182, 0.2)',
    overflow: 'hidden'
};

const modalHeaderStyle = {
    padding: '20px 30px',
    borderBottom: '1px solid #e9ecef',
    backgroundColor: '#f8f9fa',
    borderTopLeftRadius: '12px',
    borderTopRightRadius: '12px'
};

const headerContentStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '20px'
};

const titleSectionStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
    flex: 1
};

const modalTitleStyle = {
    margin: 0,
    fontSize: '22px',
    fontWeight: '600',
    color: '#2c3e50'
};

const counterStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '2px',
    backgroundColor: '#ecf0f1',
    padding: '8px 16px',
    borderRadius: '20px'
};

const currentCounterStyle = {
    fontSize: '18px',
    fontWeight: 'bold',
    color: '#9b59b6'
};

const totalCounterStyle = {
    fontSize: '16px',
    color: '#7f8c8d'
};

const closeButtonStyle = {
    padding: '10px 20px',
    backgroundColor: 'rgba(231, 76, 60, 0.1)',
    border: '1px solid #e74c3c',
    borderRadius: '6px',
    color: '#e74c3c',
    fontSize: '14px',
    fontWeight: 'bold',
    cursor: 'pointer',
    transition: 'all 0.2s',
    '&:hover': {
        backgroundColor: '#e74c3c',
        color: 'white'
    }
};

const gifDisplayContainer = {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    padding: '40px',
    backgroundColor: '#f5f5f5'
};

const loadingContainerStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center'
};

const loadingTextStyle = {
    marginTop: '15px',
    fontSize: '18px',
    color: '#7f8c8d'
};

const errorContainerStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    padding: '40px'
};

const errorIconStyle = {
    fontSize: '60px',
    marginBottom: '20px'
};

const errorTextStyle = {
    fontSize: '18px',
    color: '#e74c3c',
    marginBottom: '20px'
};

const loadedGifContainer = {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
};

const gifImageFullStyle = {
    maxWidth: '100%',
    maxHeight: 'calc(90vh - 200px)',
    objectFit: 'contain',
    boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
    backgroundColor: 'white',
    borderRadius: '8px'
};

const navArrowStyle = (direction) => ({
    position: 'absolute',
    top: '50%',
    [direction]: '30px',
    transform: 'translateY(-50%)',
    width: '60px',
    height: '60px',
    backgroundColor: 'rgba(155, 89, 182, 0.9)',
    border: 'none',
    borderRadius: '50%',
    color: 'white',
    fontSize: '24px',
    fontWeight: 'bold',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.3s',
    '&:hover': {
        backgroundColor: '#8e44ad',
        transform: 'translateY(-50%) scale(1.1)'
    }
});

const modalFooterStyle = {
    padding: '20px 30px',
    borderTop: '1px solid #e9ecef',
    backgroundColor: '#f8f9fa'
};

const footerControlsStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '15px'
};

const gifSelectorStyle = {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
    justifyContent: 'center'
};

const gifSelectorButtonStyle = {
    width: '40px',
    height: '40px',
    borderRadius: '8px',
    border: '1px solid #dee2e6',
    cursor: 'pointer',
    fontWeight: 'bold',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    transition: 'all 0.2s',
    '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 3px 10px rgba(0,0,0,0.1)'
    }
};

const loadedIndicatorStyle = {
    position: 'absolute',
    top: '2px',
    right: '2px',
    fontSize: '8px',
    color: '#27ae60'
};

const errorIndicatorStyle = {
    position: 'absolute',
    top: '2px',
    right: '2px',
    fontSize: '8px',
    color: '#e74c3c'
};

const footerInfoStyle = {
    textAlign: 'center'
};

const infoTextStyle = {
    fontSize: '12px',
    color: '#7f8c8d',
    margin: '0 0 5px 0'
};

const gifInfoStyle = {
    fontSize: '11px',
    color: '#95a5a6',
    margin: 0
};

export default PPTViewer;